import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  MapPin,
  Check,
  X,
  Clock,
  Building2,
  User,
  Calendar,
  ArrowRight,
  RefreshCw,
  Map,
  Eye,
} from "lucide-react";
import { API } from "@/App";
import { formatDateTR } from "@/utils/dateUtils";
import { useTranslation } from "react-i18next";

export default function AddressChangeRequestsModule({ onBadgeUpdate }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin"]);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [filter, setFilter] = useState("pending");
  
  // Map modal state
  const [showMapModal, setShowMapModal] = useState(false);
  const [mapRequest, setMapRequest] = useState(null);
  
  // Modal state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchRequests();
    fetchStats();
  }, [filter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/address-change-requests?status=${filter}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setRequests(data);
      }
    } catch (error) {
      toast.error(t("common:toast_taleplerYuklenemedi"));
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/admin/address-change-requests/stats`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Stats fetch error:", error);
    }
  };

  const handleApprove = async (requestId) => {
    setProcessing(true);
    try {
      const response = await fetch(`${API}/admin/address-change-requests/${requestId}/approve`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ note: "Onaylandı" }),
      });
      
      if (response.ok) {
        toast.success(t("admin:toast_adresDegisikligiOnaylandiİsletmeSahibine"));
        fetchRequests();
        fetchStats();
        // Sidebar badge'ini güncelle
        if (onBadgeUpdate) onBadgeUpdate();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("admin:approveFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Red nedeni belirtmelisiniz");
      return;
    }
    
    setProcessing(true);
    try {
      const response = await fetch(`${API}/admin/address-change-requests/${selectedRequest.request_id}/reject`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reason: rejectReason }),
      });
      
      if (response.ok) {
        toast.success(t("admin:toast_adresDegisikligiReddedildiİsletmeSahibine"));
        setShowRejectModal(false);
        setSelectedRequest(null);
        setRejectReason("");
        fetchRequests();
        fetchStats();
        // Sidebar badge'ini güncelle
        if (onBadgeUpdate) onBadgeUpdate();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("admin:rejectFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setProcessing(false);
    }
  };

  const openRejectModal = (request) => {
    setSelectedRequest(request);
    setRejectReason("");
    setShowRejectModal(true);
  };

  const openMapModal = (request) => {
    setMapRequest(request);
    setShowMapModal(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge className="bg-amber-100 text-amber-800">{t('common:pending')}</Badge>;
      case "approved":
        return <Badge className="bg-green-100 text-green-800">{t('admin:approved')}</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-800">{t("admin:rejected")}</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card 
          className={`border-0 shadow-sm cursor-pointer transition-all ${filter === "pending" ? "ring-2 ring-amber-500" : ""}`}
          onClick={() => setFilter("pending")}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-grey">Bekleyen</p>
                <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`border-0 shadow-sm cursor-pointer transition-all ${filter === "approved" ? "ring-2 ring-green-500" : ""}`}
          onClick={() => setFilter("approved")}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-grey">Onaylanan</p>
                <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
              </div>
              <Check className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`border-0 shadow-sm cursor-pointer transition-all ${filter === "rejected" ? "ring-2 ring-red-500" : ""}`}
          onClick={() => setFilter("rejected")}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-grey">Reddedilen</p>
                <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
              </div>
              <X className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`border-0 shadow-sm cursor-pointer transition-all ${filter === "all" ? "ring-2 ring-navy" : ""}`}
          onClick={() => setFilter("all")}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-grey">{t('common:total')}</p>
                <p className="text-2xl font-bold text-navy">{stats.total || stats.pending + stats.approved + stats.rejected}</p>
              </div>
              <MapPin className="w-8 h-8 text-navy" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Requests List */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Adres Değişikliği Talepleri
            </CardTitle>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { fetchRequests(); fetchStats(); }}
              className="rounded-xl"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Yenile
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-grey">{t('common:loading')}</div>
          ) : requests.length === 0 ? (
            <div className="text-center py-8 text-grey">
              {filter === "pending" ? t("common:noPendingRequests") : t("common:noRequestsFound")}
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((request) => (
                <div
                  key={request.request_id}
                  className="p-4 border rounded-xl hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Header */}
                      <div className="flex items-center gap-3 mb-3">
                        <Building2 className="w-5 h-5 text-navy" />
                        <span className="font-semibold">{request.business_name}</span>
                        {getStatusBadge(request.status)}
                      </div>
                      
                      {/* Owner Info */}
                      <div className="flex items-center gap-2 text-sm text-grey mb-3">
                        <User className="w-4 h-4" />
                        <span>{request.owner_name}</span>
                        <span className="text-slate-300">|</span>
                        <span>{request.owner_email}</span>
                      </div>
                      
                      {/* Address Change */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-xs text-grey mb-1">Mevcut Adres</p>
                          <p className="text-sm font-medium">{request.current_data?.address || request.current_address || "-"}</p>
                          {(request.current_data?.latitude || request.current_latitude) && (
                            <p className="text-xs text-grey mt-1">
                              {(request.current_data?.latitude || request.current_latitude)?.toFixed(4)}, {(request.current_data?.longitude || request.current_longitude)?.toFixed(4)}
                            </p>
                          )}
                        </div>
                        
                        <div className="p-3 bg-amber-50 rounded-lg relative">
                          <ArrowRight className="absolute -left-6 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500 hidden md:block" />
                          <p className="text-xs text-amber-600 mb-1">Talep Edilen Adres</p>
                          <p className="text-sm font-medium">{request.proposed_data?.address || "-"}</p>
                          {request.proposed_data?.latitude && (
                            <p className="text-xs text-amber-600 mt-1">
                              {request.proposed_data.latitude?.toFixed(4)}, {request.proposed_data.longitude?.toFixed(4)}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Map Button */}
                      {(request.current_data?.latitude || request.current_latitude) && request.proposed_data?.latitude && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mb-3 rounded-xl"
                          onClick={() => openMapModal(request)}
                          data-testid={`map-btn-${request.request_id}`}
                        >
                          <Map className="w-4 h-4 mr-2" />
                          Haritada Karşılaştır
                        </Button>
                      )}
                      
                      {/* Date */}
                      <div className="flex items-center gap-2 text-xs text-grey">
                        <Calendar className="w-3 h-3" />
                        <span>Talep: {formatDateTR(request.created_at)}</span>
                        {request.updated_at && request.status !== "pending" && (
                          <>
                            <span className="text-slate-300">|</span>
                            <span>İşlem: {formatDateTR(request.updated_at)}</span>
                          </>
                        )}
                      </div>
                      
                      {/* Review Note */}
                      {request.review_note && request.status !== "pending" && (
                        <div className={`mt-3 p-2 rounded-lg text-sm ${
                          request.status === "approved" ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"
                        }`}>
                          <strong>{request.status === "approved" ? "Not:" : "Red Nedeni:"}</strong> {request.review_note}
                        </div>
                      )}
                    </div>
                    
                    {/* Actions */}
                    {request.status === "pending" && (
                      <div className="flex flex-col gap-2 ml-4">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 rounded-xl"
                          onClick={() => handleApprove(request.request_id)}
                          disabled={processing}
                          data-testid={`approve-btn-${request.request_id}`}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Onayla
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50 rounded-xl"
                          onClick={() => openRejectModal(request)}
                          disabled={processing}
                          data-testid={`reject-btn-${request.request_id}`}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Reddet
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <X className="w-5 h-5" />
              Adres Değişikliğini Reddet
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-slate-50 rounded-xl">
              <p className="text-sm text-grey">İşletme:</p>
              <p className="font-medium">{selectedRequest?.business_name}</p>
            </div>
            
            <div>
              <Label>Red Nedeni *</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder={t("admin:ph_nedenBuTalebiReddediyorsunuzİsletme")}
                className="rounded-xl mt-1"
                rows={4}
                data-testid="reject-reason-input"
              />
              <p className="text-xs text-grey mt-1">
                Bu mesaj işletme sahibine bildirim olarak gönderilecektir.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => setShowRejectModal(false)}
              >
                İptal
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 rounded-xl"
                onClick={handleReject}
                disabled={processing || !rejectReason.trim()}
                data-testid="confirm-reject-btn"
              >
                {processing ? t("common:processing") : t("admin:rejectAndNotify")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Map Comparison Modal */}
      <Dialog open={showMapModal} onOpenChange={setShowMapModal}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Map className="w-5 h-5 text-navy" />
              Konum Karşılaştırması - {mapRequest?.business_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Legend */}
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                <span>Mevcut Konum</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-amber-500 rounded-full"></div>
                <span>Talep Edilen Konum</span>
              </div>
            </div>

            {/* Map - Using OpenStreetMap iframe */}
            {mapRequest ? (
              <div className="relative w-full h-[300px] rounded-xl overflow-hidden border border-slate-200">
                <iframe
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${Math.min(
                    (mapRequest.current_data?.longitude || mapRequest.current_longitude || 28.9784),
                    (mapRequest.proposed_data?.longitude || 28.9784)
                  ) - 0.005}%2C${Math.min(
                    (mapRequest.current_data?.latitude || mapRequest.current_latitude || 41.0082),
                    (mapRequest.proposed_data?.latitude || 41.0082)
                  ) - 0.005}%2C${Math.max(
                    (mapRequest.current_data?.longitude || mapRequest.current_longitude || 28.9784),
                    (mapRequest.proposed_data?.longitude || 28.9784)
                  ) + 0.005}%2C${Math.max(
                    (mapRequest.current_data?.latitude || mapRequest.current_latitude || 41.0082),
                    (mapRequest.proposed_data?.latitude || 41.0082)
                  ) + 0.005}&layer=mapnik&marker=${mapRequest.proposed_data?.latitude || 41.0082}%2C${mapRequest.proposed_data?.longitude || 28.9784}`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                />
                <div className="absolute bottom-2 left-2 right-2 bg-white/95 p-2 rounded-lg text-xs">
                  <div className="flex justify-between">
                    <span className="text-blue-600">🔵 Mevcut: {(mapRequest.current_data?.latitude || mapRequest.current_latitude)?.toFixed(4)}, {(mapRequest.current_data?.longitude || mapRequest.current_longitude)?.toFixed(4)}</span>
                    <span className="text-amber-600">🟠 Talep: {mapRequest.proposed_data?.latitude?.toFixed(4)}, {mapRequest.proposed_data?.longitude?.toFixed(4)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full h-[300px] bg-slate-100 rounded-xl flex items-center justify-center">
                <p className="text-grey">Harita yükleniyor...</p>
              </div>
            )}

            {/* Address Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-xl">
                <p className="text-sm text-blue-600 font-medium mb-1">Mevcut Adres</p>
                <p className="text-sm">{mapRequest?.current_data?.address || mapRequest?.current_address || "-"}</p>
                <p className="text-xs text-grey mt-2">
                  {(mapRequest?.current_data?.latitude || mapRequest?.current_latitude)?.toFixed(6)}, 
                  {(mapRequest?.current_data?.longitude || mapRequest?.current_longitude)?.toFixed(6)}
                </p>
              </div>
              <div className="p-4 bg-amber-50 rounded-xl">
                <p className="text-sm text-amber-600 font-medium mb-1">Talep Edilen Adres</p>
                <p className="text-sm">{mapRequest?.proposed_data?.address || "-"}</p>
                <p className="text-xs text-grey mt-2">
                  {mapRequest?.proposed_data?.latitude?.toFixed(6)}, 
                  {mapRequest?.proposed_data?.longitude?.toFixed(6)}
                </p>
              </div>
            </div>

            {/* Actions */}
            {mapRequest?.status === "pending" && (
              <div className="flex gap-3 pt-2">
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700 rounded-xl"
                  onClick={() => {
                    handleApprove(mapRequest.request_id);
                    setShowMapModal(false);
                  }}
                  disabled={processing}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Onayla
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-red-300 text-red-600 hover:bg-red-50 rounded-xl"
                  onClick={() => {
                    setShowMapModal(false);
                    openRejectModal(mapRequest);
                  }}
                >
                  <X className="w-4 h-4 mr-2" />
                  Reddet
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
